// steps/UserSignupVerifyOtpStep.tsx

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import CustomOtpInput from '@/components/form/CustomOtpInput';
import { UserSignupVerifyOtpFormValues, userSignupVerifyOtpSchema } from '../schemas/userSignupVerifyOtpSchema';
import axios from 'axios';
import { useApp } from '@/contexts/AppContext';
import { UserSignupState, UserSignupAction } from '../states/userSignupReducer';
import ErrorMessage from '@/components/common/ErrorMessage';
import { BasicResponse } from '@/core/models/common-types';
import { extractErrorMessage } from '@/core/utils/utils';

interface UserSignupVerifyOtpStepProps {
  id: string;
  onNext: (data: UserSignupVerifyOtpFormValues) => void;
  onPrevious: () => void;
  state: UserSignupState;
  dispatch: React.Dispatch<UserSignupAction>;
}

const UserSignupVerifyOtpStep: React.FC<UserSignupVerifyOtpStepProps> = ({
  onNext,
  onPrevious,
  dispatch,
  state,
}) => {
  const [error, setError] = useState<BasicResponse | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  const { baseUrl } = useApp();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UserSignupVerifyOtpFormValues>({
    resolver: zodResolver(userSignupVerifyOtpSchema),
    defaultValues: {
      otpCode: '',
      id: state.id,
      sessionId: state.sessionId,
    },
  });

  useEffect(() => {
    console.log('Form Errors =>', errors);
  }, [errors]);

  useEffect(() => {
    if (secondsRemaining <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setSecondsRemaining((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsRemaining]);

  const maskPhoneNumber = (phoneNumber?: string) => {
    if (!phoneNumber) return '';

    if (phoneNumber.length <= 6) {
      return phoneNumber;
    }

    return `${phoneNumber.slice(0, 3)}******${phoneNumber.slice(-3)}`;
  };

  const maskEmail = (email?: string) => {
    if (!email) return '';

    const [name, domain] = email.split('@');

    if (!name || !domain) {
      return email;
    }

    return `${name.substring(0, Math.min(2, name.length))}******@${domain}`;
  };

  const channelText =
    state.otpChannel === 1
      ? 'SMS'
      : state.otpChannel === 2
        ? 'Email'
        : 'Selected Channel';

  const recipient =
    state.otpChannel === 1
      ? maskPhoneNumber(state.phoneNumber)
      : maskEmail(state.email);

  const handleResendOtp = async () => {
    try {
      setIsResending(true);

      const { data } = await axios.post<{
        id: string;
        sessionId: string;
      }>(`${baseUrl}api/v2/UserSignup/ResendOtp`, {
        id: state.id,
        sessionId: state.sessionId,
      });

      dispatch({
        type: 'UPDATE',
        payload: {
          sessionId: data.sessionId,
        },
      });

      setSecondsRemaining(60);
    } catch (error) {
      setError(extractErrorMessage(error));
    } finally {
      setIsResending(false);
    }
  };

  const onSubmit = async (data: UserSignupVerifyOtpFormValues) => {
    try {
      const { data: result } = await axios.post<{
        otherNames: string;
        surname: string;
        firstName: string;
      }>(`${baseUrl}api/v2/UserSignup/VerifyOtp`, data);

      dispatch({
        type: 'UPDATE',
        payload: {
          ...data,
          isVerified: true,
          otherNames: result.otherNames,
          surname: result.surname,
          firstName: result.firstName,
        },
      });

      onNext(data);
    } catch (error) {
      setError(extractErrorMessage(error));
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <ErrorMessage error={error} onClose={() => setError(null)} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body py-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-shield-check text-primary"></i>
                  </div>

                  <div>
                    <div className="fw-semibold">
                      Verification Code Sent
                    </div>

                    <div className="small text-muted">
                      Via <strong>{channelText}</strong> to{' '}
                      <strong>{recipient}</strong>
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={onPrevious}
                  >
                    <i className="bi bi-pencil-square me-1"></i>
                    Change
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleResendOtp}
                    disabled={isResending || secondsRemaining > 0}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>

                    {isResending
                      ? 'Sending...'
                      : secondsRemaining > 0
                        ? `Resend (${secondsRemaining}s)`
                        : 'Resend'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <CustomOtpInput
            control={control}
            name="otpCode"
            label="OTP Code"
            required
          />

          <div className="d-flex justify-content-end mt-4">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Verifying...' : 'Verify OTP'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserSignupVerifyOtpStep;